import { useCallback, useMemo, useState } from "react";
import { getSectionText } from "../data/sectionTexts";
import s from "./Sample.module.scss";
import Title from "../Title/Title";
import { scheduleActivities } from "../i18n/schedule";
import { customizeText } from "../i18n/customize";
import { useLanguage } from "../contexts/LanguageContext";
import { img, imageSrcSet, imagePreset } from "../utils/cloudinaryImage";
import { trackEvent } from "../utils/analytics";
import ImageLightbox, {
  ImageZoomButton,
} from "../components/ImageLightbox/ImageLightbox";

const BASE_GROUP_PRICE = 25000;
const SOLO_BASE_PRICE = 25000;
const INCLUDED_PEOPLE = 4;
const EXTRA_PERSON_PRICE = 20000;
const MAX_PEOPLE = 6;
const BASE_TOUR_IMAGE = "v1783928733/DSC00191_isylmv.webp";
const STAY_NIGHTS = [1, 2, 3, 4];
const PHOTO_GOAL_PRESETS = {
  landscape: {
    width: 520,
    height: 360,
    crop: "fill",
    quality: "auto",
  },
  portrait: {
    width: 420,
    height: 560,
    crop: "fill",
    quality: "auto",
  },
};

const CUSTOM_UI = {
  JP: {
    photoQuestion: "どんな写真が撮りたい？",
    linkedPlanLabel: "おすすめプラン",
    stayQuestion: "何泊する予定？",
    peopleQuestion: "人数を選択してください",
    nightSuffix: "泊",
    dayPrefix: "",
    daySuffix: "日目",
    freeDayTitle: "未定",
    freeDayText: "選んだ写真が増えるとここにプランが入ります。",
    hotelDayTitle: "ホテルでゆっくり",
    hotelDayText: "予定を詰め込まずゆっくりする",
    scheduleTitleSuffix: "のスケジュール",
    noScheduleText: "この日は予定を入れず、ゆっくり過ごせます。",
    tripNote: "滞在日数に合わせて提案します。",
  },
  EN: {
    photoQuestion: "What kind of photos do you want?",
    linkedPlanLabel: "Recommended plan",
    stayQuestion: "How many nights?",
    peopleQuestion: "Select number of guests",
    nightSuffix: " nights",
    dayPrefix: "Day ",
    daySuffix: "",
    freeDayTitle: "Free day",
    freeDayText:
      "Use this day for travel, cafés, or beach time. Add more photo styles to fill the plan.",
    hotelDayTitle: "Relax at the hotel",
    hotelDayText:
      "Keep this day open for a slow hotel, beach, or café day without packing the schedule.",
    scheduleTitleSuffix: " Schedule",
    noScheduleText: "Keep this day open and enjoy a slow day.",
    tripNote:
      "We split the experiences across your stay so the day does not feel overloaded.",
  },
  中文: {
    photoQuestion: "想拍什么样的照片？",
    linkedPlanLabel: "推荐行程",
    stayQuestion: "计划住几晚？",
    peopleQuestion: "请选择人数",
    nightSuffix: "晚",
    dayPrefix: "第",
    daySuffix: "天",
    freeDayTitle: "自由活动日",
    freeDayText:
      "可安排移动、咖啡店或海边时间。选择更多照片类型后会自动加入行程。",
    hotelDayTitle: "在酒店悠闲度过",
    hotelDayText: "不安排太满，可以在酒店、海边或咖啡店轻松度过。",
    scheduleTitleSuffix: "行程",
    noScheduleText: "这一天可以不安排体验，轻松度过。",
    tripNote: "不会把所有体验塞进同一天，会根据停留天数分开安排。",
  },
  한국어: {
    photoQuestion: "어떤 사진을 남기고 싶나요?",
    linkedPlanLabel: "추천 플랜",
    stayQuestion: "몇 박 예정인가요?",
    peopleQuestion: "인원을 선택해 주세요",
    nightSuffix: "박",
    dayPrefix: "",
    daySuffix: "일차",
    freeDayTitle: "자유 일정",
    freeDayText:
      "이동, 카페, 해변 시간을 여유롭게 보낼 수 있습니다. 사진 스타일을 더 선택하면 일정이 추가됩니다.",
    hotelDayTitle: "호텔에서 여유롭게",
    hotelDayText:
      "일정을 무리하게 넣지 않고 호텔, 해변, 카페에서 천천히 보낼 수 있습니다.",
    scheduleTitleSuffix: " 일정",
    noScheduleText: "이날은 일정을 넣지 않고 여유롭게 보낼 수 있습니다.",
    tripNote:
      "하루에 모두 넣지 않고, 체류 일정에 맞춰 체험을 나누어 제안합니다.",
  },
};

const PHOTO_GOAL_ITEMS = [
  {
    id: "scenic",
    emoji: "📷",
    image: "v1784682749/DJI_20260721090315_0012_D_ndyr5p.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "宮古島らしい絶景写真",
      EN: "Scenic Miyako photos",
      中文: "宫古岛绝景照片",
      한국어: "미야코지마 절경 사진",
    },
    text: {
      JP: "秘境スポットや撮影スポットを巡り、ドローンと一眼で残します。",
      EN: "Visit scenic and hidden spots with drone and camera shooting.",
      中文: "巡游秘境和绝景拍摄点，用无人机和相机留下回忆。",
      한국어: "숨은 명소와 절경 스팟을 돌며 드론과 카메라로 촬영합니다.",
    },
  },
  {
    id: "scenic-camera-blue",
    emoji: "📷",
    image: "v1785311449/DSC00588-2_x2m1d1.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "秘境フォトツアーで残す海写真",
      EN: "Scenic tour ocean photos",
      中文: "绝景行程海景照片",
      한국어: "절경 투어 바다 사진",
    },
  },
  {
    id: "sup",
    emoji: "🌊",
    image: "v1784734130/DJI_20260722152642_0033_D_tb9qki.webp",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "海の上でSUP写真",
      EN: "SUP photos on the water",
      中文: "海上 SUP 照片",
      한국어: "바다 위 SUP 사진",
    },
    text: {
      JP: "人が少ない秘境スポットで、SUP体験と撮影を楽しめます。",
      EN: "Enjoy SUP and photos at a quiet hidden ocean spot.",
      中文: "在安静的秘境海域体验 SUP 并拍摄照片。",
      한국어: "한적한 숨은 바다에서 SUP 체험과 촬영을 즐깁니다.",
    },
  },
  {
    id: "sup-sunset-drone-august",
    emoji: "🌊",
    image: "v1785774919/DJI_20260801191604_0048_D_t2h5kf.webp",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPで残す夕方の海",
      EN: "Evening ocean SUP photos",
      中文: "秘境 SUP 夕方海景",
      한국어: "숨은 SUP 저녁 바다 사진",
    },
  },
  {
    id: "star",
    emoji: "✨",
    image: "v1784187848/image_bdd0ur.webp",
    orientation: "portrait",
    planIds: ["star"],
    title: {
      JP: "星空と人物写真",
      EN: "Starry sky portraits",
      中文: "星空人物照",
      한국어: "별하늘 인물 사진",
    },
    text: {
      JP: "満天の星空の下で、旅の最後に残したい一枚を撮影します。",
      EN: "Capture a special portrait under Miyakojima’s starry sky.",
      中文: "在满天星空下，拍下旅途中最特别的一张照片。",
      한국어: "별이 가득한 밤하늘 아래 특별한 한 장을 남깁니다.",
    },
  },
  {
    id: "drone",
    emoji: "🚁",
    image: "v1784682749/DJI_20260721090156_0009_D_al9eb0.webp",
    orientation: "portrait",
    planIds: ["base"],
    title: {
      JP: "ドローンで残す海",
      EN: "Drone ocean shots",
      中文: "无人机海景照片",
      한국어: "드론으로 남기는 바다",
    },
    text: {
      JP: "宮古ブルーを上空から。秘境フォトツアー内で撮影します。",
      EN: "Capture Miyako blue from above during the scenic tour.",
      中文: "从空中记录宫古蓝，包含在绝景行程内。",
      한국어: "미야코 블루를 하늘에서 담는 절경 투어 촬영입니다.",
    },
  },
  {
    id: "secret-blue",
    emoji: "🌴",
    image: "v1783928437/DJI_20260707115907_0008_D_xag4ot.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "秘境の海写真",
      EN: "Hidden ocean photos",
      中文: "秘境海景照片",
      한국어: "숨은 바다 사진",
    },
    text: {
      JP: "人が少ないスポットで宮古島らしい海を残します。",
      EN: "Capture quiet hidden ocean spots around Miyakojima.",
      中文: "在人少的秘境留下宫古岛海景。",
      한국어: "한적한 숨은 바다에서 미야코지마다운 사진을 남깁니다.",
    },
  },
  {
    id: "sup-portrait",
    emoji: "🌊",
    image: "v1784268598/DSC00189_pslhwt.webp",
    orientation: "portrait",
    planIds: ["sup"],
    title: {
      JP: "SUPの人物写真",
      EN: "SUP portrait photos",
      中文: "SUP 人像照片",
      한국어: "SUP 인물 사진",
    },
    text: {
      JP: "SUP体験中の自然な写真を残します。",
      EN: "Capture natural portraits during the SUP experience.",
      中文: "记录 SUP 体验中的自然人像。",
      한국어: "SUP 체험 중 자연스러운 인물 사진을 남깁니다.",
    },
  },
  {
    id: "star-portrait",
    emoji: "✨",
    image: "v1784266422/DSC00400-2_ddg5if.jpg",
    orientation: "portrait",
    planIds: ["star"],
    title: {
      JP: "星空シルエット",
      EN: "Starry silhouette",
      中文: "星空剪影",
      한국어: "별하늘 실루엣",
    },
    text: {
      JP: "星空を背景に印象的な一枚を撮影します。",
      EN: "Create a memorable shot under the stars.",
      中文: "以星空为背景拍摄特别的一张。",
      한국어: "별하늘을 배경으로 인상적인 한 장을 촬영합니다.",
    },
  },
  {
    id: "beach-view",
    emoji: "🏖️",
    image: "v1784466423/DJI_20260718160638_0015_D_gh8w0n.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "ビーチの絶景写真",
      EN: "Scenic beach photos",
      中文: "绝景海滩照片",
      한국어: "비치 절경 사진",
    },
    text: {
      JP: "宮古島のビーチを巡って撮影します。",
      EN: "Visit and shoot Miyakojima’s beautiful beaches.",
      中文: "巡游并拍摄宫古岛美丽海滩。",
      한국어: "미야코지마의 아름다운 해변을 돌며 촬영합니다.",
    },
  },
  {
    id: "base-sup-ocean",
    emoji: "🌊",
    image: "v1785311757/IMG_8564_jkfm7r.webp",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "透明感のある海写真",
      EN: "Clear ocean photos",
      中文: "透明海景照片",
      한국어: "투명한 바다 사진",
    },
  },
  {
    id: "base-sup-summer-dslr-1",
    emoji: "📷",
    image: "v1785776522/DSC00817_ag3z9t.webp",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "海辺で残す夏の写真",
      EN: "Summer ocean photos",
      中文: "海边夏日照片",
      한국어: "해변 여름 사진",
    },
  },
  {
    id: "base-sup-summer-dslr-2",
    emoji: "📷",
    image: "v1785776522/DSC00843_co2jqa.webp",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "自然体の海フォト",
      EN: "Natural ocean portraits",
      中文: "自然海边人像",
      한국어: "자연스러운 바다 사진",
    },
  },
  {
    id: "base-sup-drone-august",
    emoji: "🚁",
    image: "v1785776522/DJI_20260802131800_0033_D_drlb1i.webp",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "宮古ブルーの空撮",
      EN: "Miyako blue aerial shot",
      中文: "宫古蓝空拍",
      한국어: "미야코 블루 항공 사진",
    },
  },
  {
    id: "base-sup-summer-dslr-3",
    emoji: "📷",
    image: "v1785776523/DSC00815_yry3re.webp",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "旅の空気を残す海写真",
      EN: "Ocean travel memories",
      中文: "旅行氛围海景照片",
      한국어: "여행 분위기의 바다 사진",
    },
  },
  {
    id: "star-moment-august",
    emoji: "✨",
    image: "v1786120675/DSC00888_tk8uhe.webp",
    orientation: "landscape",
    planIds: ["star"],
    title: {
      JP: "星空撮影で残す写真",
      EN: "Starry sky photo memories",
      中文: "星空摄影照片",
      한국어: "별하늘 촬영 사진",
    },
  },
  {
    id: "sup-clear-water",
    emoji: "🌊",
    image: "v1785311438/DSC00522_vyppts.webp",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPの海写真",
      EN: "Hidden SUP ocean photos",
      中文: "秘境 SUP 海景",
      한국어: "숨은 SUP 바다 사진",
    },
  },
  {
    id: "secret-portrait",
    emoji: "📷",
    image: "v1741535462/20250307215516_b70rgq.webp",
    orientation: "portrait",
    planIds: ["base"],
    title: {
      JP: "秘境で残す人物写真",
      EN: "Hidden spot portraits",
      中文: "秘境人像照片",
      한국어: "숨은 명소 인물 사진",
    },
  },
  {
    id: "base-sup-beach",
    emoji: "🌴",
    image: "v1784767636/IMG_5428_oepvhn.jpg",
    orientation: "portrait",
    planIds: ["base", "sup"],
    title: {
      JP: "海辺で残す自然な写真",
      EN: "Natural beach photos",
      中文: "海边自然照片",
      한국어: "해변 자연 사진",
    },
  },
  {
    id: "sup-vertical",
    emoji: "🌊",
    image: "v1784767309/IMG_5481_ie849d.jpg",
    orientation: "portrait",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPの人物写真",
      EN: "Hidden SUP portraits",
      中文: "秘境 SUP 人像",
      한국어: "숨은 SUP 인물 사진",
    },
  },
  {
    id: "sup-drone-wide",
    emoji: "🚁",
    image: "v1784766585/DJI_20260722154007_0060_D_azbuxt.webp",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "SUPを空から残す写真",
      EN: "SUP from above",
      中文: "空拍 SUP 照片",
      한국어: "하늘에서 담는 SUP",
    },
  },
  {
    id: "sup-blue-drone",
    emoji: "🚁",
    image: "v1785005393/DJI_20260722152001_0021_D_small_odgfk0.jpg",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPドローン写真",
      EN: "Hidden SUP drone photos",
      中文: "秘境 SUP 空拍",
      한국어: "숨은 SUP 드론 사진",
    },
  },
  {
    id: "base-sup-candid-1",
    emoji: "📷",
    image: "v1785005395/DSC00157_small_rnomp6.jpg",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "旅の空気を残す写真",
      EN: "Travel mood photos",
      中文: "旅行氛围照片",
      한국어: "여행 분위기 사진",
    },
  },
  {
    id: "base-sup-candid-2",
    emoji: "📷",
    image: "v1785005394/DSC00161_small_f5qnkn.jpg",
    orientation: "landscape",
    planIds: ["base", "sup"],
    title: {
      JP: "自然体の撮影",
      EN: "Natural candid photos",
      中文: "自然抓拍",
      한국어: "자연스러운 스냅",
    },
  },
  {
    id: "sup-portrait-water",
    emoji: "🌊",
    image: "v1785005393/DSC00178_small_hexny7.jpg",
    orientation: "portrait",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPだけの写真",
      EN: "Hidden SUP-only photos",
      中文: "秘境 SUP 专属照片",
      한국어: "숨은 SUP 전용 사진",
    },
  },
  {
    id: "sup-rock-blue",
    emoji: "🌊",
    image: "v1785005393/DSC00191_small_lhfvnk.jpg",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "岩場と宮古ブルー",
      EN: "Rocks and Miyako blue",
      中文: "岩石与宫古蓝",
      한국어: "바위와 미야코 블루",
    },
  },
  {
    id: "base-drone-shore",
    emoji: "🚁",
    image: "v1784766584/DJI_20260715152007_0040_D_rrrztf.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "海岸線のドローン写真",
      EN: "Coastline drone photos",
      中文: "海岸线空拍",
      한국어: "해안선 드론 사진",
    },
  },
  {
    id: "base-dslr-portrait",
    emoji: "📷",
    image: "v1784945639/DSC00440-2_syxyio.webp",
    orientation: "portrait",
    planIds: ["base"],
    title: {
      JP: "一眼で残す絶景写真",
      EN: "Camera portraits",
      中文: "相机人像照片",
      한국어: "카메라 인물 사진",
    },
  },
  {
    id: "base-hidden-rock",
    emoji: "🌴",
    image: "v1784268196/image10_xcny11.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "秘境スポット写真",
      EN: "Hidden location photos",
      中文: "秘境景点照片",
      한국어: "숨은 명소 사진",
    },
  },
  {
    id: "sup-blue-cove",
    emoji: "🌊",
    image: "v1785004077/DJI_20260707125430_0025_D_small_r4mnrd.jpg",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPの青い海",
      EN: "Blue hidden SUP spot",
      中文: "秘境 SUP 蓝海",
      한국어: "숨은 SUP 푸른 바다",
    },
  },
  {
    id: "base-secret-point",
    emoji: "🌴",
    image: "v1784266410/DJI_20260714133703_0069_D_cp1tbk.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "秘境フォトツアーの秘境写真",
      EN: "Scenic hidden tour photos",
      中文: "绝景行程秘境照片",
      한국어: "절경 투어 숨은 사진",
    },
  },
  {
    id: "base-blue-drone-2",
    emoji: "🚁",
    image: "v1784766584/DJI_20260715125937_0011_D_ovrkkz.webp",
    orientation: "landscape",
    planIds: ["base"],
    title: {
      JP: "宮古ブルーのドローン写真",
      EN: "Miyako blue drone photos",
      中文: "宫古蓝空拍",
      한국어: "미야코 블루 드론 사진",
    },
  },
  {
    id: "sup-secret-drone",
    emoji: "🚁",
    image: "v1785003708/DJI_20260707132223_0047_D_small_no9d5s.jpg",
    orientation: "landscape",
    planIds: ["sup"],
    title: {
      JP: "秘境SUPの空撮",
      EN: "Hidden SUP aerial photos",
      中文: "秘境 SUP 空拍",
      한국어: "숨은 SUP 항공 사진",
    },
  },
];

const PHOTO_GOALS = [
  PHOTO_GOAL_ITEMS.find((item) => item.id === "secret-blue"),
  PHOTO_GOAL_ITEMS.find((item) => item.id === "base-sup-summer-dslr-2"),
  PHOTO_GOAL_ITEMS.find((item) => item.id === "star-portrait"),
  PHOTO_GOAL_ITEMS.find((item) => item.id === "scenic"),
  PHOTO_GOAL_ITEMS.find((item) => item.id === "sup"),
  PHOTO_GOAL_ITEMS.find((item) => item.id === "star-moment-august"),
  ...PHOTO_GOAL_ITEMS.filter(
    (item) =>
      ![
        "secret-blue",
        "base-sup-summer-dslr-2",
        "star-portrait",
        "scenic",
        "sup",
        "star-moment-august",
        "star",
      ].includes(item.id),
  ),
].filter((item) => item && !item.planIds.includes("sup"));

const LANG_ALIASES = {
  ZH: "中文",
  CN: "中文",
  zh: "中文",
  cn: "中文",
  KO: "한국어",
  KR: "한국어",
  ko: "한국어",
  kr: "한국어",
};

const resolveLang = (lang) => {
  if (scheduleActivities[lang]) return lang;

  const alias = LANG_ALIASES[lang];
  if (alias && scheduleActivities[alias]) return alias;

  return "JP";
};

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Z" />
    <path d="M12 14c-3.31 0-6 1.79-6 4v1c0 .55.45 1 1 1h10c.55 0 1-.45 1-1v-1c0-2.21-2.69-4-6-4Z" />
  </svg>
);

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 6l1.5-2h5L16 6" />
    <rect x="3" y="6.5" width="18" height="13" rx="3" />
    <circle cx="12" cy="13" r="3.6" />
    <path d="M19.5 4.5l.8-.8" />
    <path d="M4.5 4.5l-.8-.8" />
    <path d="M21 8h1.2" />
  </svg>
);

const formatPrice = (price) => {
  return `¥${price.toLocaleString()}`;
};

const formatGroupPrice = (price, text) => {
  return `${formatPrice(price)} / ${text.perGroup}`;
};

const localize = (value, lang) => value?.[lang] || value?.JP || "";

const unique = (items) => Array.from(new Set(items));

const DAY_SCHEDULES = {
  JP: {
    base: [
      ["9:30", "お迎え", "ご宿泊先までお迎えに伺います。"],
      [
        "10:00",
        "絶景撮影ツアー",
        "秘境スポットや撮影スポットを巡りながら撮影します。",
      ],
      ["12:30", "写真・映像確認", "撮影したデータを確認しながらお渡しします。"],
      ["13:00", "送迎・解散", "ご宿泊先またはご希望の場所までお送りします。"],
    ],
    sup: [
      ["9:30", "お迎え", "海況に合わせたポイントへ向かいます。"],
      ["10:00", "秘境SUP", "人が少ない海でSUP体験と撮影を行います。"],
      ["12:30", "写真・映像確認", "撮影データを確認してお渡しします。"],
      ["13:00", "送迎・解散", "ご宿泊先またはご希望の場所までお送りします。"],
    ],
    baseSup: [
      ["9:30", "お迎え", "ご宿泊先までお迎えに伺います。"],
      [
        "10:00",
        "絶景撮影ツアー",
        "秘境スポットや撮影スポットを巡りながら撮影します。",
      ],
      [
        "11:30",
        "秘境SUP",
        "秘境フォトツアーの中で、海況に合わせたポイントへ移動してSUP体験と撮影を行います。",
      ],
      ["13:30", "写真・映像確認", "撮影したデータを確認しながらお渡しします。"],
      ["14:00", "送迎・解散", "ご宿泊先またはご希望の場所までお送りします。"],
    ],
    star: [
      [
        "20:00",
        "お迎え・星空ポイントへ移動",
        "星が見えやすい場所へご案内します。",
      ],
      ["20:30", "星空撮影", "星空と人物写真を撮影します。"],
      ["21:30", "送迎・解散", "撮影後、ご宿泊先までお送りします。"],
    ],
  },
  EN: {
    base: [
      ["9:30", "Pickup", "We will pick you up at your accommodation."],
      ["10:00", "Scenic photo tour", "Visit hidden and scenic photo spots."],
      ["12:30", "Photo review", "Review the photo and video data."],
      [
        "13:00",
        "Drop-off",
        "We will take you back to your hotel or preferred spot.",
      ],
    ],
    sup: [
      ["9:30", "Pickup", "Head to the best SUP spot for the conditions."],
      [
        "10:00",
        "Hidden SUP",
        "Enjoy SUP and photo shooting at a quiet ocean spot.",
      ],
      ["12:30", "Photo review", "Review and prepare the shooting data."],
      [
        "13:00",
        "Drop-off",
        "We will take you back to your hotel or preferred spot.",
      ],
    ],
    baseSup: [
      ["9:30", "Pickup", "We will pick you up at your accommodation."],
      ["10:00", "Scenic photo tour", "Visit hidden and scenic photo spots."],
      [
        "11:30",
        "Hidden SUP",
        "Move to the best ocean spot and enjoy SUP as part of the scenic tour.",
      ],
      ["13:30", "Photo review", "Review and prepare the shooting data."],
      [
        "14:00",
        "Drop-off",
        "We will take you back to your hotel or preferred spot.",
      ],
    ],
    star: [
      ["20:00", "Meet and move", "Head to a good starry sky location."],
      ["20:30", "Starry sky photo", "Shoot portraits under the stars."],
      ["21:30", "Drop-off", "We will take you back after the shoot."],
    ],
  },
};

const FREE_DAY_SUGGESTIONS = {
  JP: [
    {
      id: "drive",
      title: "ドライブ・宮古島を周り写真撮影",
      text: "宮古島を車で周りながら、橋・ビーチ・絶景スポットで写真を残す",
      schedule: [
        [
          "10:00",
          "宮古島ドライブ",
          "伊良部大橋や来間大橋など、宮古島らしい景色を見ながら移動します。",
        ],
        [
          "12:00",
          "ビーチ・絶景スポット",
          "その日の天気に合わせて、写真に残しやすい海や橋のスポットへ。",
        ],
        [
          "15:00",
          "カフェ休憩",
          "撮影の合間に、近くのカフェでゆっくり過ごすのもおすすめです。",
        ],
      ],
    },
    {
      id: "cafe",
      title: "カフェ巡り / サンセット",
      text: "撮影におすすめのカフェ: Seahorse club cafe / OHAMAテラス / Pani Pani / ブルーシール パイナガマ店 / 島cafeとぅんからや / あおぞらパーラー / HARRY'S Shrimp Truck / ブルータートルファーム マンゴーカフェ / Ninufa",
      schedule: [
        [
          "10:00",
          "撮影におすすめのカフェ",
          "Seahorse club cafe / OHAMAテラス / Pani Pani / ブルーシール パイナガマ店 / Ninufa",
        ],
        [
          "11:00",
          "カフェ巡り",
          "島cafeとぅんからや / あおぞらパーラー / HARRY'S Shrimp Truck / ブルータートルファーム マンゴーカフェ など、写真に残したい場所へ。",
        ],
        [
          "14:00",
          "写真・散策",
          "カフェ周辺のビーチや景色もゆっくり楽しめます。",
        ],
        [
          "18:30",
          "サンセット",
          "夕方は海辺でサンセットを見ながら、宮古島らしい景色を楽しみます。",
        ],
      ],
    },
    {
      id: "night",
      title: "八重干瀬ツアー / 宮古島グルメと夜遊びを満喫",
      text: "午前中は八重干瀬ツアーへ。夜は宮古島のグルメや、夜遊びを満喫",
      schedule: [
        [
          "9:30",
          "八重干瀬ツアー",
          "朝は八重干瀬ツアーで宮古ブルーの海を楽しみます。",
        ],
        [
          "18:30",
          "おすすめの夜ご飯屋",
          "島おでんたから / 美ら島 / でいりぐち / うまりずま / 焼肉喜八 / しゃぶしゃぶ 嘉福",
        ],
        [
          "20:30",
          "夜遊びスポット / 夜カフェ",
          "朝まで営業してるバー / 果実屋。バーや夜カフェなど宮古島の夜を満喫。",
        ],
      ],
    },
    {
      id: "beach",
      title: "人が少ないビーチでのんびり",
      text: "観光客が行かない人が少ないビーチをこっそり教えます。",
      schedule: [
        [
          "10:00",
          "静かなビーチへ",
          "観光客が行かない人が少ないビーチをこっそり教えます。",
        ],
        [
          "13:00",
          "海辺でゆっくり",
          "潮位と風向きで行き先を変えると、より綺麗な海に出会えます。",
        ],
      ],
    },
    {
      id: "sunset",
      title: "サンセットを見る",
      text: "夕方は海辺でサンセットを見ながら、無理なく宮古島の景色を楽しむ",
      schedule: [
        [
          "16:30",
          "サンセットスポットへ",
          "17END / 佐和田の浜 / トゥリバーなど、その日の空に合わせて移動します。",
        ],
        [
          "18:00",
          "夕方の散歩",
          "夕方の散歩ではサンセットを見るのがおすすめです。",
        ],
      ],
    },
  ],
  EN: [
    {
      id: "cafe",
      title: "Café hopping",
      text: "Spend a slow day visiting cafés with beautiful views and island atmosphere.",
      schedule: [
        [
          "10:00",
          "Head to a café",
          "Start slowly and visit a café with a nice view.",
        ],
        [
          "11:00",
          "Café hopping",
          "Enjoy ocean-view cafés and relaxed island spaces.",
        ],
        [
          "14:00",
          "Photos and walk",
          "Take photos and enjoy the beach or scenery nearby.",
        ],
      ],
    },
    {
      id: "night",
      title: "Dinner and nightlife",
      text: "Enjoy island food, bars, or a relaxed evening walk.",
      schedule: [
        ["18:30", "Dinner", "Try local Miyakojima food."],
        [
          "20:30",
          "Evening walk",
          "Enjoy bars, cafés, or the night atmosphere.",
        ],
      ],
    },
    {
      id: "beach",
      title: "Relax at the beach",
      text: "Keep the day open and spend time at a beautiful beach.",
      schedule: [
        ["10:00", "Go to the beach", "Head to an easy-to-access beach."],
        ["11:00", "Beach time", "Swim, take photos, or relax on the sand."],
        ["14:00", "Café break", "Enjoy a nearby café at your own pace."],
      ],
    },
    {
      id: "hotel",
      title: "Relax at the hotel",
      text: "Leave the day open for your hotel, pool, and a slow stay.",
      schedule: [
        [
          "10:00",
          "Slow hotel morning",
          "Relax after breakfast in your room or by the pool.",
        ],
        ["13:00", "Lunch and rest", "Enjoy an easy lunch around the hotel."],
        [
          "18:00",
          "Watch the sunset",
          "Enjoy the sunset by the ocean in the evening.",
        ],
      ],
    },
  ],
};

const getFreeDaySuggestion = (lang, index) => {
  const suggestions = FREE_DAY_SUGGESTIONS[lang] || FREE_DAY_SUGGESTIONS.JP;
  const priority = ["night", "cafe", "beach", "sunset"];
  const orderedSuggestions = [
    ...priority
      .map((id) => suggestions.find((suggestion) => suggestion.id === id))
      .filter(Boolean),
    ...suggestions.filter((suggestion) => !priority.includes(suggestion.id)),
  ];

  return orderedSuggestions[index % orderedSuggestions.length];
};

const getEmptyPlanSuggestion = (lang, index) => {
  const suggestions = FREE_DAY_SUGGESTIONS[lang] || FREE_DAY_SUGGESTIONS.JP;
  const priority = ["drive", "night", "cafe", "beach", "sunset"];
  const orderedSuggestions = [
    ...priority
      .map((id) => suggestions.find((suggestion) => suggestion.id === id))
      .filter(Boolean),
    ...suggestions.filter((suggestion) => !priority.includes(suggestion.id)),
  ];

  return orderedSuggestions[index % orderedSuggestions.length];
};

const buildDaySchedule = (day, ui, lang) => {
  const schedules = DAY_SCHEDULES[lang] || DAY_SCHEDULES.JP;

  if (!day?.plans?.length) {
    if (day?.suggestion?.schedule) {
      return day.suggestion.schedule.map(([time, title, text], index) => ({
        id: `${day.id}-${day.suggestion.id}-${index}`,
        time,
        title,
        text,
      }));
    }

    return [
      {
        id: `${day?.id || "day"}-free`,
        time: "",
        title: day?.dayNumber === 4 ? ui.hotelDayTitle : ui.freeDayTitle,
        text: day?.dayNumber === 4 ? ui.hotelDayText : ui.noScheduleText,
      },
    ];
  }

  const planIds = day.plans.map((plan) => plan.id);
  const hasBase = planIds.includes("base");
  const hasSup = planIds.includes("sup");
  const hasStar = planIds.includes("star");

  if (hasBase && hasSup) {
    const mergedSteps = [...(schedules.baseSup || schedules.base)];

    if (hasStar) {
      mergedSteps.push(...schedules.star);
    }

    return mergedSteps.map(([time, title, stepText], index) => ({
      id: `${day.id}-merged-${index}`,
      time,
      title,
      text: stepText,
    }));
  }

  return day.plans.flatMap((plan) => {
    const steps = schedules[plan.id] || schedules.base;

    return steps.map(([time, title, stepText], index) => ({
      id: `${day.id}-${plan.id}-${index}`,
      time,
      title,
      text: stepText,
    }));
  });
};

const SampleImage = ({
  src,
  alt,
  preset = imagePreset.optionCard,
  sizes = "(max-width: 768px) 86vw, 260px",
}) => {
  return (
    <img
      src={img(src, preset)}
      srcSet={imageSrcSet(src, preset, [520, 720, 960])}
      sizes={sizes}
      alt={alt}
      loading="lazy"
      fetchPriority="low"
      decoding="async"
      width={preset.width}
      height={preset.height}
    />
  );
};

const Sample = () => {
  const { lang } = useLanguage();

  const currentLang = resolveLang(lang);
  const titleText = getSectionText(currentLang, "customize");
  const text = customizeText[currentLang] || customizeText.JP;
  const ui = CUSTOM_UI[currentLang] || CUSTOM_UI.JP;
  const activityList = (
    scheduleActivities[currentLang] || scheduleActivities.JP
  ).filter((item) => item.id !== "uni" && item.id !== "sunset");

  const [selectedPhotoIds, setSelectedPhotoIds] = useState(["secret-blue"]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [stayNights, setStayNights] = useState(2);
  const [activeDayNumber, setActiveDayNumber] = useState(1);
  const [people, setPeople] = useState(2);

  const formatPeopleCount = (count) => {
    if (currentLang === "EN") {
      const unit = count === 1 ? text.peopleSingular : text.peoplePlural;
      return `${count} ${unit}`;
    }

    return `${count}${text.peopleUnit}`;
  };

  const selectedPhotoGoals = useMemo(() => {
    return PHOTO_GOALS.filter((item) => selectedPhotoIds.includes(item.id));
  }, [selectedPhotoIds]);

  const selectedPlanIds = useMemo(() => {
    return unique(selectedPhotoGoals.flatMap((item) => item.planIds));
  }, [selectedPhotoGoals]);

  const baseSelected = selectedPlanIds.includes("base");

  const selectedIds = useMemo(() => {
    return selectedPlanIds.filter((id) => id !== "base");
  }, [selectedPlanIds]);

  const selectedActivities = useMemo(() => {
    return activityList.filter((item) => selectedIds.includes(item.id));
  }, [activityList, selectedIds]);

  const getActivityPricePerPerson = useCallback((item) => {
    if (people === 1 && item.soloPricePerPerson) {
      return item.soloPricePerPerson;
    }

    return item.pricePerPerson || 0;
  }, [people]);

  const selectedPlans = useMemo(() => {
    const basePlan = baseSelected
      ? [
          {
            id: "base",
            title: text.baseTourTitle,
            shortText: text.baseTourText,
            image: BASE_TOUR_IMAGE,
            displayPrice: formatGroupPrice(
              people === 1 ? SOLO_BASE_PRICE : BASE_GROUP_PRICE,
              text,
            ),
          },
        ]
      : [];

    return [
      ...basePlan,
      ...selectedActivities.map((item) => ({
        id: item.id,
        title: item.title,
        shortText: item.shortText,
        image: item.image,
        displayPrice:
          getActivityPricePerPerson(item) > 0
            ? `${formatPrice(getActivityPricePerPerson(item))} / ${text.perPerson}`
            : text.noExtraFee,
      })),
    ];
  }, [baseSelected, getActivityPricePerPerson, people, selectedActivities, text]);

  const getPhotoPlanLabel = (item) => {
    const labels = item.planIds
      .map((id) => {
        if (id === "base") return text.baseTourTitle;
        return activityList.find((activity) => activity.id === id)?.title;
      })
      .filter(Boolean);

    return labels.join(" / ");
  };

  const selectedSummaryItems = useMemo(() => {
    return selectedPlans.map((item) => item.title);
  }, [selectedPlans]);

  const dayPlans = useMemo(() => {
    const dayCount = stayNights;
    const days = Array.from({ length: dayCount }, (_, index) => ({
      id: `day-${index + 1}`,
      dayNumber: index + 1,
      plans: [],
      suggestion: null,
    }));

    selectedPlans.forEach((plan, index) => {
      const dayIndex = Math.min(index, dayCount - 1);
      days[dayIndex].plans.push(plan);
    });

    let suggestionIndex = 0;

    return days.map((day) => {
      if (day.plans.length > 0) return day;

      const suggestion =
        selectedPlans.length === 0
          ? getEmptyPlanSuggestion(currentLang, suggestionIndex)
          : getFreeDaySuggestion(currentLang, suggestionIndex);
      suggestionIndex += 1;

      return {
        ...day,
        suggestion,
      };
    });
  }, [currentLang, selectedPlans, stayNights]);

  const visibleActiveDayNumber = dayPlans.some(
    (day) => day.dayNumber === activeDayNumber,
  )
    ? activeDayNumber
    : dayPlans[0]?.dayNumber || 1;
  const activeDayPlan = dayPlans.find(
    (day) => day.dayNumber === visibleActiveDayNumber,
  );
  const activeDaySchedule = useMemo(() => {
    return buildDaySchedule(activeDayPlan, ui, currentLang);
  }, [activeDayPlan, currentLang, ui]);
  const activeDayScheduleTitle = useMemo(() => {
    if (!activeDayPlan?.plans?.length) {
      return activeDayPlan?.suggestion?.title || ui.freeDayTitle;
    }

    const planTitle = activeDayPlan.plans.map((plan) => plan.title).join(" / ");

    return `${planTitle}${ui.scheduleTitleSuffix}`;
  }, [activeDayPlan, ui]);

  const optionPricePerPerson = useMemo(() => {
    return selectedActivities.reduce((sum, item) => {
      return sum + getActivityPricePerPerson(item);
    }, 0);
  }, [getActivityPricePerPerson, selectedActivities]);

  const optionTotalPrice = optionPricePerPerson * people;

  const extraPeopleCount = Math.max(people - INCLUDED_PEOPLE, 0);
  const extraPeoplePrice = baseSelected
    ? extraPeopleCount * EXTRA_PERSON_PRICE
    : 0;

  const basePrice = baseSelected
    ? people === 1
      ? SOLO_BASE_PRICE
      : BASE_GROUP_PRICE
    : 0;

  const totalPrice = basePrice + optionTotalPrice + extraPeoplePrice;

  const pricePerPersonTotal = Math.ceil(totalPrice / people);

  const totalPriceDetails = [
    baseSelected
      ? `${text.baseTourTitle} ${formatGroupPrice(basePrice, text)}`
      : null,
    baseSelected && people === 1 ? text.soloDiscount : null,
    ...selectedActivities
      .filter((item) => getActivityPricePerPerson(item) > 0)
      .map(
        (item) => `${item.title} ${formatPrice(getActivityPricePerPerson(item) * people)}`,
      ),
    extraPeoplePrice > 0
      ? `${text.extraPeoplePrice} ${formatPrice(extraPeoplePrice)}`
      : null,
  ].filter(Boolean);

  const handlePhotoSelect = (id) => {
    const photoGoal = PHOTO_GOALS.find((item) => item.id === id);

    setSelectedPhotoIds((current) => {
      if (current.includes(id)) {
        trackEvent("customize_photo_goal_toggle", {
          event_category: "customize",
          event_label: id,
          photo_goal_id: id,
          photo_goal_title: localize(photoGoal?.title, currentLang),
          selected: false,
          people,
        });

        return current.filter((item) => item !== id);
      }

      trackEvent("customize_photo_goal_toggle", {
        event_category: "customize",
        event_label: id,
        photo_goal_id: id,
        photo_goal_title: localize(photoGoal?.title, currentLang),
        selected: true,
        people,
      });

      return [...current, id];
    });
  };

  const handleStayNightsChange = (nights) => {
    setStayNights(nights);
    setActiveDayNumber((current) => Math.min(current, nights));

    trackEvent("customize_stay_nights_change", {
      event_category: "customize",
      event_label: String(nights),
      stay_nights: nights,
      people,
    });
  };

  const handleDayTabClick = (dayNumber) => {
    setActiveDayNumber(dayNumber);

    trackEvent("customize_day_schedule_tab_click", {
      event_category: "customize",
      event_label: String(dayNumber),
      day_number: dayNumber,
      people,
    });
  };

  const handleMinusPeople = () => {
    setPeople((current) => {
      const nextPeople = Math.max(1, current - 1);

      if (nextPeople !== current) {
        trackEvent("customize_people_change", {
          event_category: "customize",
          event_label: "decrease",
          people: nextPeople,
        });
      }

      return nextPeople;
    });
  };

  const handlePlusPeople = () => {
    setPeople((current) => {
      const nextPeople = Math.min(MAX_PEOPLE, current + 1);

      if (nextPeople !== current) {
        trackEvent("customize_people_change", {
          event_category: "customize",
          event_label: "increase",
          people: nextPeople,
        });
      }

      return nextPeople;
    });
  };

  return (
    <section className={s.root}>
      <Title kicker={titleText.kicker} title={titleText.title} />

      <p className={s.lead}>
        {text.leadLines.map((line, index) => (
          <span key={line}>
            {line}
            {index !== text.leadLines.length - 1 && <br />}
          </span>
        ))}
      </p>

      <div className={s.panel}>
        <div className={s.sectionHead}>
          <div>
            <span className={s.number}>01</span>
            <h3>{ui.photoQuestion}</h3>
          </div>

          <div className={s.headActions}>
            <div className={s.decor}>
              <span className={s.handwriting}>
                Memories that last a lifetime
              </span>
              <span className={s.camera}>
                <CameraIcon />
              </span>
            </div>
          </div>
        </div>

        <p className={s.topNote}>{text.topNote}</p>

        <div className={s.optionList}>
          {PHOTO_GOALS.map((item) => {
            const isActive = selectedPhotoIds.includes(item.id);
            const label = localize(item.title, currentLang);

            return (
              <article
                key={item.id}
                className={`${s.optionCard} ${s.photoGoalCard} ${
                  item.orientation === "portrait"
                    ? s.portraitGoal
                    : s.landscapeGoal
                } ${isActive ? s.active : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handlePhotoSelect(item.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handlePhotoSelect(item.id);
                  }
                }}
                aria-pressed={isActive}
              >
                <SampleImage
                  src={item.image}
                  alt={label}
                  preset={
                    PHOTO_GOAL_PRESETS[item.orientation] ||
                    PHOTO_GOAL_PRESETS.landscape
                  }
                  sizes={
                    item.orientation === "portrait"
                      ? "(max-width: 768px) 34vw, 150px"
                      : "(max-width: 768px) 46vw, 190px"
                  }
                />

                <span className={s.check}>{isActive ? "✓" : ""}</span>
                <ImageZoomButton
                  className={s.photoZoomButton}
                  label="拡大"
                  ariaLabel={`${label}を大きく見る`}
                  onClick={() => setEnlargedImage(item.image)}
                />
                <span className={s.photoPlanLabel}>
                  {getPhotoPlanLabel(item)}
                </span>
              </article>
            );
          })}
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.sectionHead}>
          <div>
            <span className={s.number}>02</span>
            <h3>{text.scheduleTitle}</h3>

            <p className={s.scheduleNotice}>{ui.tripNote}</p>

            <div className={s.staySelector} aria-label={ui.stayQuestion}>
              <span>{ui.stayQuestion}</span>

              <div>
                {STAY_NIGHTS.map((nights) => (
                  <button
                    key={nights}
                    type="button"
                    className={stayNights === nights ? s.activeStay : ""}
                    onClick={() => handleStayNightsChange(nights)}
                  >
                    {nights}
                    {ui.nightSuffix}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={s.dayPlanGrid}>
          {dayPlans.map((day) => {
            return (
              <article className={s.dayPlanCard} key={day.id}>
                <div className={s.dayPlanHead}>
                  <span>
                    {ui.dayPrefix}
                    {day.dayNumber}
                    {ui.daySuffix}
                  </span>
                  <strong>
                    {day.plans.length > 0
                      ? day.plans.map((plan) => plan.title).join(" / ")
                      : day.suggestion?.title || ui.freeDayTitle}
                  </strong>
                </div>

                <div className={s.dayPlanBody}>
                  {day.plans.length > 0 ? (
                    day.plans.map((plan) => (
                      <div className={s.dayPlanItem} key={plan.id}>
                        <SampleImage
                          src={plan.image}
                          alt={plan.title}
                          preset={imagePreset.timeline}
                          sizes="(max-width: 768px) 300px, 220px"
                        />

                        <div>
                          <h4>{plan.title}</h4>
                          <p>{plan.shortText}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className={s.freeDayText}>
                      {day.suggestion?.text || ui.freeDayText}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className={s.daySchedule}>
          {dayPlans.length > 1 && (
            <div className={s.dayTabs}>
              {dayPlans.map((day) => (
                <button
                  key={day.id}
                  type="button"
                  className={
                    day.dayNumber === visibleActiveDayNumber ? s.activeDay : ""
                  }
                  onClick={() => handleDayTabClick(day.dayNumber)}
                >
                  {ui.dayPrefix}
                  {day.dayNumber}
                  {ui.daySuffix}
                </button>
              ))}
            </div>
          )}

          <div className={s.daySchedulePanel}>
            <div className={s.dayScheduleHead}>
              <span>
                {ui.dayPrefix}
                {visibleActiveDayNumber}
                {ui.daySuffix}
              </span>
              <h4>{activeDayScheduleTitle}</h4>
            </div>

            <div className={s.dayScheduleList}>
              {activeDaySchedule.map((step) => (
                <div className={s.dayScheduleStep} key={step.id}>
                  <time>{step.time}</time>

                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={s.priceControls}>
          <span>{ui.peopleQuestion}</span>

          <div className={s.peopleControl}>
            <span className={s.peopleLabel}>
              <PeopleIcon />
            </span>

            <button
              type="button"
              onClick={handleMinusPeople}
              disabled={people <= 1}
              aria-label={text.decreasePeopleLabel}
            >
              −
            </button>

            <strong>{formatPeopleCount(people)}</strong>

            <button
              type="button"
              onClick={handlePlusPeople}
              disabled={people >= MAX_PEOPLE}
              aria-label={text.increasePeopleLabel}
            >
              ＋
            </button>
          </div>
        </div>

        <div className={s.priceSummary}>
          <div className={`${s.summaryItem} ${s.selectedSummary}`}>
            <span
              className={`${s.summaryIcon} ${selectedSummaryItems.length === 0 ? s.inactiveIcon : ""}`}
            >
              {selectedSummaryItems.length > 0 ? "✓" : "−"}
            </span>

            <p>
              <span className={s.summaryLabel}>
                {selectedSummaryItems.length > 0
                  ? text.selectedBaseLabel
                  : text.unselectedBaseLabel}
              </span>

              {selectedSummaryItems.length > 0 ? (
                <span className={s.selectedChips}>
                  {selectedSummaryItems.map((item) => (
                    <span className={s.selectedChip} key={item}>
                      {item}
                    </span>
                  ))}
                </span>
              ) : (
                <span className={s.emptySelection}>-</span>
              )}
            </p>
          </div>

          <div className={s.summaryItem}>
            <span className={`${s.summaryIcon} ${s.peopleIcon}`}>
              <PeopleIcon />
            </span>

            <p>
              {text.peopleLabel} <strong>{formatPeopleCount(people)}</strong>
              {baseSelected && people === 1 && (
                <small className={s.soloDiscountNote}>
                  {text.soloDiscountApplied}
                </small>
              )}
              {baseSelected && people >= 5 && (
                <small className={s.extraPeopleNote}>
                  {text.extraPeopleNote}
                </small>
              )}
            </p>
          </div>

          <div className={s.mainPrice}>
            <span>{text.pricePerPersonTotal}</span>
            <strong>{formatPrice(pricePerPersonTotal)}</strong>
          </div>

          <div className={s.totalPrice}>
            <span>
              {currentLang === "EN"
                ? `${formatPeopleCount(people)} ${text.totalSuffix}`
                : `${formatPeopleCount(people)}${text.totalSuffix}`}
            </span>

            <strong>{formatPrice(totalPrice)}</strong>

            {totalPriceDetails.length > 0 && (
              <small>{totalPriceDetails.join(text.priceJoiner)}</small>
            )}
          </div>
        </div>
      </div>

      <ImageLightbox
        image={enlargedImage}
        alt="Tour photo preview"
        onClose={() => setEnlargedImage(null)}
      />
    </section>
  );
};

export default Sample;
